import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BatcheetLogo from './BatcheetLogo';
import { Mail, RefreshCw, Send, LogOut, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

const VerifyEmailScreen = () => {
    const { currentUser, resendVerificationEmail, signOut } = useAuth();
    const [checking, setChecking] = useState(false);
    const [resending, setResending] = useState(false);
    const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

    const handleCheckVerification = async () => {
        if (!currentUser) return;
        setChecking(true);
        try {
            await currentUser.reload();
            if (currentUser.emailVerified) {
                toast.success("Email verified! Welcome to Batcheet 🎉");
                window.location.reload();
            } else {
                toast.error("Email not verified yet. Please check your inbox or spam folder.");
            }
        } catch (err) {
            console.error("Check verification error:", err);
            toast.error("Could not check verification status. Please try again.");
        } finally {
            setChecking(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        try {
            await resendVerificationEmail();
            toast.success("Verification email resent! Check your inbox.");
        } catch (err) {
            console.error("Resend error:", err);
            toast.error("Could not resend email. Please wait a minute and try again.");
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="min-h-screen w-full bg-slate-50 dark:bg-[#0b0f17] text-slate-900 dark:text-slate-100 flex items-center justify-center p-4 sm:p-6 relative font-sans transition-colors">
            {/* Ambient Background Glow */}
            <div className="absolute top-1/4 -left-32 w-96 h-96 bg-sky-500/10 rounded-full filter blur-[100px] pointer-events-none" />
            <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-indigo-500/10 rounded-full filter blur-[100px] pointer-events-none" />

            <div className="relative z-10 w-full max-w-md bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl text-center space-y-6 animate-scale-in">
                {/* Logo & Mail Icon */}
                <div className="flex flex-col items-center space-y-3">
                    <div className="relative">
                        <BatcheetLogo size={56} className="drop-shadow-md" />
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-sky-500 text-white rounded-full flex items-center justify-center shadow-md border-2 border-white dark:border-[#111827]">
                            <Mail className="w-3.5 h-3.5" />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
                            Verify Your Email
                        </h2>
                        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                            You must verify your email address to access Batcheet.
                        </p>
                    </div>
                </div>

                {/* Email Display Card */}
                <div className="p-3.5 bg-slate-50 dark:bg-slate-900/60 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-1">
                    <p className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                        Verification sent to
                    </p>
                    <p className="text-sm font-bold text-sky-600 dark:text-sky-400 font-mono break-all">
                        {currentUser?.email}
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2.5">
                    <button
                        onClick={handleCheckVerification}
                        disabled={checking}
                        className="w-full py-3 px-4 bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white font-bold rounded-2xl text-xs sm:text-sm shadow-md shadow-sky-500/20 active:scale-98 transition-all flex items-center justify-center gap-2"
                    >
                        {checking ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                            <CheckCircle2 className="w-4 h-4" />
                        )}
                        <span>{checking ? "Checking Status..." : "I've Verified My Email"}</span>
                    </button>

                    <button
                        onClick={handleResend}
                        disabled={resending}
                        className="w-full py-2.5 px-4 bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold rounded-2xl text-xs active:scale-98 transition-all flex items-center justify-center gap-2"
                    >
                        <Send className="w-3.5 h-3.5" />
                        <span>{resending ? "Sending Link..." : "Resend Verification Link"}</span>
                    </button>
                </div>

                {/* Sign Out Action with 2-click Confirmation */}
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80">
                    {showSignOutConfirm ? (
                        <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-2xl space-y-2 animate-fade-in">
                            <p className="text-xs text-rose-600 dark:text-rose-400 font-semibold">
                                Are you sure you want to sign out?
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowSignOutConfirm(false)}
                                    className="flex-1 py-1.5 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={signOut}
                                    className="flex-1 py-1.5 bg-rose-500 text-white rounded-xl text-xs font-bold"
                                >
                                    Confirm Sign Out
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowSignOutConfirm(true)}
                            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-rose-500 transition-colors py-1"
                        >
                            <LogOut className="w-3.5 h-3.5" />
                            <span>Sign out and use another email</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifyEmailScreen;
